import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import UsbIcon from '@mui/icons-material/Usb';
import {
  createScreen,
  deleteScreen,
  describeApiError,
  downloadUsbBundle,
  getCritSchedule,
  issueEnrolmentCode,
  listBundles,
  listScreens,
  publishBundle,
  reloadScreen,
  updateScreen,
  type CritRoom,
  type KioskBundle,
  type KioskScreen,
  type KioskScreenInput,
  type ScreenMode,
} from '../lib/touchscreens';

const MODE_LABEL: Record<ScreenMode, string> = {
  room: 'Single room',
  picker: 'Room picker',
  browse: 'Browse all projects',
};

function ago(iso: string | null) {
  if (!iso) return 'never';
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return new Date(iso).toLocaleString('en-GB');
}

function size(bytes: number) {
  if (bytes > 1e9) return `${(bytes / 1e9).toFixed(1)} GB`;
  if (bytes > 1e6) return `${(bytes / 1e6).toFixed(0)} MB`;
  return `${Math.round(bytes / 1e3)} KB`;
}

/**
 * Admin tools → Touchscreens. Publish the content the venue screens show (schedule,
 * shortlisted entries, images, A2 boards) and watch every screen: online, which
 * bundle it has, download progress, errors. Admin only — this decides what the
 * public sees, so it's deliberately not open to emap staff or PR.
 */
export function Touchscreens() {
  const [screens, setScreens] = useState<KioskScreen[]>([]);
  const [bundles, setBundles] = useState<KioskBundle[]>([]);
  const [rooms, setRooms] = useState<CritRoom[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ id: string | null; input: KioskScreenInput } | null>(null);
  const [code, setCode] = useState<{ screen: KioskScreen; code: string; expires: string } | null>(null);
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [publishing, setPublishing] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [s, b] = await Promise.all([listScreens(), listBundles()]);
      setScreens(s);
      setBundles(b);
    } catch {
      // Transient poll failure — next tick recovers.
    }
  }, []);

  useEffect(() => {
    void refresh();
    getCritSchedule()
      .then((s) => setRooms(s.rooms))
      .catch(() => undefined);
    const t = setInterval(() => void refresh(), 5000);
    return () => clearInterval(t);
  }, [refresh]);

  const latest = bundles.find((b) => b.status === 'ready') ?? null;
  const building = bundles.find((b) => b.status === 'building') ?? null;
  const online = screens.filter((s) => s.online).length;
  const upToDate = screens.filter((s) => latest && s.bundle_version === latest.version).length;
  const roomName = useMemo(() => new Map(rooms.map((r) => [r.id, r.name])), [rooms]);

  const publish = async () => {
    setPublishing(true);
    setError(null);
    try {
      await publishBundle(year);
      await refresh();
    } catch (e) {
      setError(describeApiError(e, 'Could not start publishing.'));
    } finally {
      setPublishing(false);
    }
  };

  const save = async () => {
    if (!editing) return;
    try {
      if (editing.id) await updateScreen(editing.id, editing.input);
      else {
        const created = await createScreen(editing.input);
        // New screen: straight to its enrolment code, which is what you need next.
        const c = await issueEnrolmentCode(created.id);
        setCode({ screen: created, code: c.code, expires: c.expires_at });
      }
      setEditing(null);
      await refresh();
    } catch (e) {
      setError(describeApiError(e, 'Could not save the screen.'));
    }
  };

  const newCode = async (s: KioskScreen) => {
    if (s.enrolled && !window.confirm(`${s.name} is already set up. A new code lets another laptop take over this screen. Continue?`)) return;
    try {
      const c = await issueEnrolmentCode(s.id);
      setCode({ screen: s, code: c.code, expires: c.expires_at });
    } catch (e) {
      setError(describeApiError(e, 'Could not create a code.'));
    }
  };

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h3" component="h1">
          Touchscreens
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          The venue screens. Each laptop runs waf-kiosk and shows the latest published content — it keeps working offline, and updates within a minute of
          publishing when it's online.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ---- Publish ---- */}
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems={{ md: 'center' }} justifyContent="space-between">
          <Box>
            <Typography variant="h6">Publish to screens</Typography>
            <Typography variant="body2" color="text.secondary">
              Packs the{' '}
              <Box component={RouterLink} to="/crit-schedule" sx={{ color: 'inherit' }}>
                crit schedule
              </Box>
              , every shortlisted entry for the year (details, images, A2 boards) and the touchscreen app. Screens download only what changed.
            </Typography>
            {latest && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Live: <strong>version {latest.version}</strong> · {ago(latest.completed_at)} · {latest.entry_count} entries · {latest.session_count} slots ·{' '}
                {size(latest.total_bytes)} · {upToDate}/{screens.length} screens have it
              </Typography>
            )}
          </Box>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexShrink: 0 }}>
            <TextField select size="small" label="Year" value={year} onChange={(e) => setYear(e.target.value)} sx={{ width: 100 }}>
              {[0, 1].map((d) => {
                const y = String(new Date().getFullYear() - d);
                return (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                );
              })}
            </TextField>
            <Button variant="contained" size="large" startIcon={<CloudUploadOutlinedIcon />} disabled={publishing || !!building} onClick={() => void publish()}>
              {building ? `Building v${building.version}…` : 'Publish'}
            </Button>
          </Stack>
        </Stack>
        {building && <LinearProgress sx={{ mt: 2 }} />}
        <BundleHistory bundles={bundles} />
      </Paper>

      {/* ---- Screens ---- */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h5">
          Screens{' '}
          <Typography component="span" color="text.secondary">
            {online}/{screens.length} online
          </Typography>
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button color="inherit" startIcon={<RefreshIcon />} disabled={!screens.length} onClick={() => void reloadScreen(null).then(refresh)}>
            Reload all
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<AddIcon />}
            onClick={() => setEditing({ id: null, input: { name: '', mode: 'room', rooms: [], day_override: null, idle_seconds: 90 } })}
          >
            Add screen
          </Button>
        </Stack>
      </Stack>

      {screens.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No screens yet. Add one for each laptop, then run setup on the laptop with its code.</Typography>
        </Paper>
      )}

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' } }}>
        {screens.map((s) => (
          <ScreenCard
            key={s.id}
            screen={s}
            latest={latest?.version ?? null}
            roomLabel={s.rooms.map((r) => roomName.get(r) ?? r).join(', ')}
            onEdit={() =>
              setEditing({ id: s.id, input: { name: s.name, mode: s.mode, rooms: s.rooms, day_override: s.day_override, idle_seconds: s.idle_seconds } })
            }
            onCode={() => void newCode(s)}
            onReload={() => void reloadScreen(s.id).then(refresh)}
            onDelete={async () => {
              if (!window.confirm(`Remove ${s.name}? The laptop stops receiving updates until it's set up again.`)) return;
              await deleteScreen(s.id);
              await refresh();
            }}
          />
        ))}
      </Box>

      {editing && (
        <ScreenDialog
          input={editing.input}
          isNew={!editing.id}
          rooms={rooms}
          onChange={(input) => setEditing({ ...editing, input })}
          onClose={() => setEditing(null)}
          onSave={() => void save()}
        />
      )}

      {code && (
        <Dialog open onClose={() => setCode(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Set up “{code.screen.name}”</DialogTitle>
          <DialogContent>
            <Stack spacing={2}>
              <Typography variant="body2">On the laptop, run setup from the USB stick and enter this code when asked:</Typography>
              <Typography sx={{ fontSize: 44, fontWeight: 800, letterSpacing: '0.12em', textAlign: 'center', fontFamily: 'monospace' }}>{code.code}</Typography>
              <Box component="pre" sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1, fontSize: 13, whiteSpace: 'pre-wrap', m: 0 }}>
                {`powershell -ExecutionPolicy Bypass -File .\\setup.ps1 -Server ${window.location.origin} -Code ${code.code}`}
              </Box>
              <Typography variant="body2" color="text.secondary">
                Works once, until {new Date(code.expires).toLocaleString('en-GB')}.
              </Typography>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button variant="contained" onClick={() => setCode(null)}>
              Done
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Stack>
  );
}

function BundleHistory({ bundles }: { bundles: KioskBundle[] }) {
  const [open, setOpen] = useState<number | null>(null);
  if (bundles.length === 0) return null;
  return (
    <Stack sx={{ mt: 2 }} divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
      {bundles.slice(0, 6).map((b) => (
        <Box key={b.version} sx={{ py: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography sx={{ width: 44, fontWeight: 600 }}>v{b.version}</Typography>
            <Chip size="small" label={b.status} color={b.status === 'ready' ? 'success' : b.status === 'failed' ? 'error' : 'default'} variant="outlined" />
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 0 }} noWrap>
              {b.status === 'failed'
                ? b.error
                : `${b.year} · ${b.entry_count} entries · ${b.session_count} slots · ${size(b.total_bytes)} · ${b.actor ?? ''} · ${ago(b.completed_at ?? b.created_at)}`}
            </Typography>
            {b.warnings.length > 0 && (
              <Button size="small" color="warning" onClick={() => setOpen(open === b.version ? null : b.version)}>
                {b.warnings.length} note{b.warnings.length === 1 ? '' : 's'}
              </Button>
            )}
            {b.status === 'ready' && (
              <Tooltip title="Download this version for a USB stick (offline fallback)">
                <IconButton size="small" aria-label="Download for USB" onClick={() => void downloadUsbBundle(b.version)}>
                  <UsbIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
          <Collapse in={open === b.version}>
            <Box sx={{ pl: 7, pt: 0.5 }}>
              {b.warnings.map((w) => (
                <Typography key={w} variant="body2" color="text.secondary">
                  {w}
                </Typography>
              ))}
            </Box>
          </Collapse>
        </Box>
      ))}
    </Stack>
  );
}

function ScreenCard({
  screen: s,
  latest,
  roomLabel,
  onEdit,
  onCode,
  onReload,
  onDelete,
}: {
  screen: KioskScreen;
  latest: number | null;
  roomLabel: string;
  onEdit: () => void;
  onCode: () => void;
  onReload: () => void;
  onDelete: () => void;
}) {
  const state = !s.enrolled ? 'setup' : s.online ? 'online' : 'offline';
  const colour = state === 'online' ? 'success.main' : state === 'offline' ? 'error.main' : 'text.disabled';
  const syncing = s.sync_state === 'downloading' && s.sync_progress;
  const [done, total] = (s.sync_progress ?? '0/0').split('/').map(Number);
  return (
    <Paper sx={{ p: 2.5 }}>
      <Stack spacing={1.25}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: colour, flexShrink: 0 }} />
          <Typography variant="h6" sx={{ flex: 1, minWidth: 0 }} noWrap>
            {s.name}
          </Typography>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={onEdit}>
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={s.enrolled ? 'New set-up code' : 'Set-up code'}>
            <IconButton size="small" onClick={onCode}>
              <KeyOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Reload the screen">
            <span>
              <IconButton size="small" onClick={onReload} disabled={!s.enrolled}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Remove">
            <IconButton size="small" onClick={onDelete}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
        <Typography variant="body2">
          {MODE_LABEL[s.mode]}
          {roomLabel && ` · ${roomLabel}`}
          {s.day_override && ` · pinned to ${s.day_override}`}
        </Typography>
        {state === 'setup' ? (
          <Typography variant="body2" color="text.secondary">
            {s.enrolment_pending ? 'Waiting for the laptop to be set up with its code.' : 'Not set up yet — get a set-up code.'}
          </Typography>
        ) : (
          <>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip size="small" label={state === 'online' ? 'Online' : `Offline · seen ${ago(s.last_seen_at)}`} color={state === 'online' ? 'success' : 'error'} variant="outlined" />
              {s.bundle_version != null ? (
                <Chip
                  size="small"
                  label={`v${s.bundle_version}${s.bundle_source === 'usb' ? ' (USB)' : ''}`}
                  color={latest != null && s.bundle_version === latest ? 'success' : 'warning'}
                  variant="outlined"
                />
              ) : (
                <Chip size="small" label="No content yet" variant="outlined" />
              )}
            </Stack>
            {syncing && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Downloading {done}/{total} files
                </Typography>
                <LinearProgress variant="determinate" value={total ? (100 * done) / total : 0} />
              </Box>
            )}
            {s.last_error && (
              <Typography variant="body2" color="error.main">
                {s.last_error}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary">
              {[s.hostname, s.last_ip, s.agent_version && `agent ${s.agent_version}`].filter(Boolean).join(' · ')}
            </Typography>
          </>
        )}
      </Stack>
    </Paper>
  );
}

function ScreenDialog({
  input,
  isNew,
  rooms,
  onChange,
  onClose,
  onSave,
}: {
  input: KioskScreenInput;
  isNew: boolean;
  rooms: CritRoom[];
  onChange: (i: KioskScreenInput) => void;
  onClose: () => void;
  onSave: () => void;
}) {
  const set = (patch: Partial<KioskScreenInput>) => onChange({ ...input, ...patch });
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isNew ? 'Add screen' : 'Edit screen'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Name" placeholder="Crit Room 7" value={input.name} onChange={(e) => set({ name: e.target.value })} autoFocus />
          <TextField select label="Shows" value={input.mode} onChange={(e) => set({ mode: e.target.value as ScreenMode, rooms: e.target.value === 'room' ? input.rooms.slice(0, 1) : input.rooms })}>
            <MenuItem value="room">A single crit room's schedule</MenuItem>
            <MenuItem value="picker">A room picker (visitor chooses the room)</MenuItem>
            <MenuItem value="browse">Browse all shortlisted projects</MenuItem>
          </TextField>
          {input.mode === 'room' && (
            <TextField select label="Room" value={input.rooms[0] ?? ''} onChange={(e) => set({ rooms: [e.target.value] })} helperText={rooms.length ? undefined : 'Import the crit schedule first to get the room list.'}>
              {rooms.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.name}
                </MenuItem>
              ))}
            </TextField>
          )}
          {input.mode === 'picker' && (
            <TextField
              select
              label="Rooms to offer (none = all)"
              value={input.rooms}
              onChange={(e) => set({ rooms: typeof e.target.value === 'string' ? e.target.value.split(',') : (e.target.value as string[]) })}
              slotProps={{ select: { multiple: true } }}
            >
              {rooms.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.name}
                </MenuItem>
              ))}
            </TextField>
          )}
          <Stack direction="row" spacing={2}>
            <TextField
              label="Pin to day (optional)"
              type="date"
              value={input.day_override ?? ''}
              onChange={(e) => set({ day_override: e.target.value || null })}
              slotProps={{ inputLabel: { shrink: true } }}
              helperText="For testing — normally the laptop's own date"
              fullWidth
            />
            <TextField
              label="Back to start after (seconds idle)"
              type="number"
              value={input.idle_seconds}
              onChange={(e) => set({ idle_seconds: Number(e.target.value) || 90 })}
              fullWidth
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={onSave} disabled={!input.name.trim() || (input.mode === 'room' && !input.rooms[0])}>
          {isNew ? 'Add and get set-up code' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
